/**
 * Login page (BUILD_SPEC.md §10.5).
 *
 * Centered form with the office name (login.officeName) above the
 * subtitle (login.subtitle). Form validation lives client-
 * side via React Hook Form + Zod; the server's auth contract (§7.3)
 * remains the source of truth and we map its error codes to friendly
 * locale-aware messages.
 *
 * Visuals on failure (§10.5): the form shakes once and the inputs get
 * a red border. The shake animation is pure CSS; we re-trigger it by
 * resetting a key on the form wrapper.
 */

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { useAuth } from "@/hooks/useAuth";
import { useAuthStore } from "@/stores/authStore";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LangToggle } from "@/components/LangToggle";

const FormSchema = z.object({
  username: z.string().trim().min(1).max(64),
  password: z.string().min(1).max(256),
});
type FormValues = z.infer<typeof FormSchema>;

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);

  // RequireAuth stashes the originally requested URL in `location.state.from`
  // when it bounces an unauth user here; after login we send them there
  // instead of always landing on /dashboard so deep links survive.
  //
  // `state.logout` is the explicit-signout signal from Header.onLogout.
  // When set, we IGNORE any from-path and always go to /dashboard so the
  // next login is a clean home state (especially important on the office
  // TV, where consecutive sessions shouldn't resume mid-task).
  //
  // We do NOT validate the from path against the current user's role —
  // the per-route guards (AdminLayout) already kick non-ADMIN users back
  // to /dashboard, so an EMPLOYEE with from=/admin/* incurs one extra
  // route transition rather than a login loop.
  const lstate = location.state as { from?: { pathname?: string }; logout?: boolean } | null;
  const redirectTo = lstate?.logout ? "/dashboard" : (lstate?.from?.pathname ?? "/dashboard");
  // Bumping `shakeKey` re-mounts the wrapper element so the CSS shake
  // animation fires anew on each failed submit. CSS animations don't
  // replay just from class re-application; a key change is the cheap
  // way to force replay without JS animation.
  const [shakeKey, setShakeKey] = useState(0);

  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
  });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await login(values.username, values.password);
      // DISPLAY (TV kiosk) always lands on the wall, ignoring any
      // from-path; every other role honors the deep-link round-trip.
      const role = useAuthStore.getState().user?.role;
      navigate(role === "DISPLAY" ? "/wall" : redirectTo, { replace: true });
    } catch (err) {
      const code = err instanceof AxiosError ? err.response?.data?.error?.code : null;
      // Map server codes (§7.3) to localized messages. INVALID_CREDENTIALS
      // covers both wrong-password and unknown-user — the server collapses
      // them on purpose to prevent enumeration.
      if (code === "ACCOUNT_LOCKED") {
        setServerError(t("login.errorLocked"));
      } else if (code === "INVALID_CREDENTIALS") {
        setServerError(t("login.errorInvalid"));
      } else {
        setServerError(t("login.errorGeneric"));
      }
      setShakeKey((k) => k + 1);
    }
  };

  const inputBase =
    "w-full rounded-md border bg-surface dark:bg-surface-dark px-3 py-2 text-fg dark:text-fg-dark focus:outline-none focus:ring-2 focus:ring-pulse focus:border-transparent";
  const inputErr = "border-rejection focus:ring-rejection";
  const inputOk = "border-hairline dark:border-hairline-dark";

  return (
    <div className="min-h-screen flex flex-col bg-canvas dark:bg-canvas-dark">
      {/* Top-right (or top-left in RTL) controls so the user can pick
          their preferred locale/theme before signing in. */}
      <div className="flex items-center justify-end gap-1 p-3">
        <LangToggle />
        <ThemeToggle />
      </div>

      <div className="flex-1 flex items-center justify-center px-4">
        <div
          key={shakeKey}
          className={`w-full max-w-sm rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-8 shadow-sm ${serverError ? "animate-shake" : ""}`}
        >
          <header className="mb-8 text-center">
            {/* Text-based logo per §10.5. Arabic office name always
                rendered so the office identity is visible in either
                locale. */}
            <h1 className="rtl-isolate text-xl font-bold text-fg dark:text-fg-dark">
              {t("login.officeName")}
            </h1>
            <p className="text-sm text-muted dark:text-muted-dark mt-3">{t("login.subtitle")}</p>
          </header>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-fg dark:text-fg-dark mb-1"
              >
                {t("common.username")}
              </label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                autoFocus
                {...register("username")}
                className={`${inputBase} ${formState.errors.username || serverError ? inputErr : inputOk}`}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-fg dark:text-fg-dark mb-1"
              >
                {t("common.password")}
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register("password")}
                className={`${inputBase} ${formState.errors.password || serverError ? inputErr : inputOk}`}
              />
            </div>

            {serverError ? (
              <p
                role="alert"
                className="text-sm font-medium text-rejection dark:text-rejection-dark"
              >
                {serverError}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={formState.isSubmitting}
              className="w-full rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark py-2.5 font-semibold disabled:opacity-60 hover:opacity-90 transition-opacity"
            >
              {formState.isSubmitting ? t("login.submitting") : t("login.submit")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
